import { KeyRound, Plug, Plus } from 'lucide-react'
import { useState } from 'react'

import type { AppConnected, AppStatus } from '@/shared/api/contracts'
import { CopyButton, RelativeTime, StatusPill } from '@/shared/components/common'
import { EmptyState, ErrorState, SkeletonRows } from '@/shared/components/data'
import { Button, Sheet } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'

import { useRotateSupportKey, useSupportAppStatuses } from '../api'
import { ActivationGuide } from './ActivationGuide'
import { ConnectAppSheet } from './ConnectAppSheet'
import { ManualForm } from './ManualForm'

export interface ConnectedAppsProps {
  /** Admin: conecta apps y rota claves. El backend lo exige igual. */
  canConnect: boolean
}

type Panel = { tipo: 'manual' | 'instalacion'; app: AppStatus }

/**
 * Las apps que tienen el chat de soporte: si ya se conectaron, qué manual
 * tiene la IA y la instalación de cada una. Un admin conecta las nuevas.
 */
export function ConnectedApps({ canConnect }: ConnectedAppsProps) {
  const t = useT()
  const estados = useSupportAppStatuses()
  const [conectando, setConectando] = useState(false)
  const [panel, setPanel] = useState<Panel | null>(null)
  const apps = estados.data ?? []

  return (
    <div className="flex flex-col gap-3 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <p className="flex-1 text-xs text-text-3">{t('support.apps.description')}</p>
        {canConnect ? (
          <Button variant="primary" onClick={() => setConectando(true)}>
            <Plus size={13} aria-hidden="true" />
            {t('support.apps.connect.title')}
          </Button>
        ) : null}
      </div>

      {estados.isPending ? (
        <SkeletonRows rows={3} columns={[0.3, 0.3, 0.2]} withSpine={false} />
      ) : estados.isError ? (
        <ErrorState error={estados.error} onRetry={() => void estados.refetch()} />
      ) : apps.length === 0 ? (
        <EmptyState
          title={t('support.apps.empty.title')}
          description={t('support.apps.empty.description')}
        />
      ) : (
        <ul aria-label={t('support.apps.listLabel')} className="flex flex-col gap-3">
          {apps.map((app) => (
            <AppCard
              key={app.id}
              app={app}
              canRotate={canConnect}
              onManual={() => setPanel({ tipo: 'manual', app })}
              onInstall={() => setPanel({ tipo: 'instalacion', app })}
            />
          ))}
        </ul>
      )}

      <ConnectAppSheet open={conectando} onClose={() => setConectando(false)} />

      <Sheet
        open={panel !== null}
        onClose={() => setPanel(null)}
        className="max-w-[640px]"
        title={
          panel?.tipo === 'manual'
            ? t('support.apps.manual.title', { app: panel.app.name })
            : panel
              ? t('support.apps.tour.title', { app: panel.app.name })
              : ''
        }
      >
        {panel?.tipo === 'manual' ? (
          <ManualForm app={panel.app} onSaved={() => setPanel(null)} />
        ) : panel ? (
          <ActivationGuide app={panel.app} />
        ) : null}
      </Sheet>
    </div>
  )
}

interface AppCardProps {
  app: AppStatus
  canRotate: boolean
  onManual: () => void
  onInstall: () => void
}

function AppCard({ app, canRotate, onManual, onInstall }: AppCardProps) {
  const t = useT()
  const rotar = useRotateSupportKey()
  const [recien, setRecien] = useState<AppConnected | null>(null)

  return (
    <li className="hairline flex flex-col rounded-[var(--radius-card)] bg-surface-1">
      <div className="flex flex-wrap items-center gap-3 px-3 py-2.5">
        <Plug size={16} aria-hidden="true" className="shrink-0 text-text-3" />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="truncate text-sm text-text-1">{app.name}</span>
          <span className="num truncate text-2xs text-text-3">
            {app.tool_slug}
            {app.linked_project_name
              ? ` · ${t('support.apps.linkedTo', { proyecto: app.linked_project_name })}`
              : null}
          </span>
        </span>

        <EstadoDeConexion app={app} />

        {app.manual ? (
          <span className="num text-2xs text-text-3">
            {t('support.apps.manual.version', {
              version: app.manual.version,
              palabras: app.manual.palabras,
            })}
          </span>
        ) : (
          <StatusPill tone="overdue" variant="quiet">
            {t('support.apps.manual.none')}
          </StatusPill>
        )}

        <div className="flex flex-wrap items-center gap-1">
          <Button variant="ghost" onClick={onManual}>
            {app.manual ? t('support.apps.manual.update') : t('support.apps.manual.load')}
          </Button>
          <Button variant="ghost" onClick={onInstall}>
            {t('support.apps.install')}
          </Button>
          {canRotate ? (
            <Button
              variant="ghost"
              disabled={rotar.isPending}
              onClick={() => rotar.mutate(app.id, { onSuccess: setRecien })}
            >
              <KeyRound size={13} aria-hidden="true" />
              {t('support.apps.rotate.action')}
            </Button>
          ) : null}
        </div>
      </div>

      {recien ? (
        <div role="status" className="hairline-t flex flex-col gap-1.5 bg-surface-2/60 px-3 py-2.5">
          <span className="text-2xs text-text-2">{t('support.apps.rotate.once')}</span>
          <div className="flex items-center gap-2">
            <code className="num min-w-0 flex-1 truncate rounded-[var(--radius-control)] bg-bg px-2 py-1 text-xs text-text-1 select-all">
              {recien.key}
            </code>
            <CopyButton value={recien.key} label={t('support.apps.key.copy')} />
            <Button variant="ghost" onClick={() => setRecien(null)}>
              {t('support.apps.key.saved')}
            </Button>
          </div>
        </div>
      ) : null}
    </li>
  )
}

function EstadoDeConexion({ app }: { app: AppStatus }) {
  const t = useT()
  if (app.conectada) {
    return (
      <span className="flex items-center gap-1.5">
        <StatusPill category="done" variant="quiet">
          {t('support.apps.status.connected')}
        </StatusPill>
        {app.last_seen_at ? (
          <RelativeTime value={app.last_seen_at} className="text-2xs text-text-3" />
        ) : null}
      </span>
    )
  }
  return (
    <StatusPill tone="not_started" variant="quiet">
      {app.key_prefix ? t('support.apps.status.waiting') : t('support.apps.status.notConnected')}
    </StatusPill>
  )
}
